/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import { JSX, useEffect, useState } from "react";
import "../../../assets/styles/custom-style.css";
import { useAuth } from "../../../auth";

import {
  listOrdersData,
  getStatus,
  viewTracking,
  ViewChatMessage,
  sentChatMessageApi,
  createDeliveryQuote,
  ShowChatHistory
} from "../core/_requests";
import { listBankData } from "../../settlements/core/_requests";
import { useNavigate } from "react-router-dom";
import MenuOrders from "./MenuOrders";
import Avatar from "react-avatar";
import { toast } from "react-toastify";
import {
  formatDateTime,
  useChef,
} from "../../../component/chef-context/chefContext";
import { OrderProps } from "../core/_models";
import moment from "moment-timezone";
import socket from "../../../component/socket";
import { useFormattedUtils } from "../../../component/dateUtils";
import ConfirmationModal from "./confirmationModal";
import LiveTrackingMap from "./Livetrack";
interface Status {
  id: number;
  name: string;
  active?: boolean;
}
interface Track {
  id: number;
  quoteId: string;
  deliveryId: string;
  trackingId: string | null;
  deliveryPartnerType: number;
  deliveryPerson: string | null;
  contactNumber: string | null;
  vehicleColor: string | null;
  vehicleType: string | null;
  driverLatitude: number | null;
  driverLongitude: number | null;
  trackingStatus: string;
  trackingURL: string;
  deliveryOtp: string;
  status: number;
}
interface reason {
  selectedReason: string;
}
// Define order types
const orderTypes = [
  { id: 1, name: "Regular" },
  { id: 3, name: "Scheduled" },
];

const Orders = () => {
  const navigate = useNavigate();
  const {
    receiverType,
    senderType,
    setOrderId,
    orders,
    setOrders,
    setReceiverType,
    messages,
    setMessages,
    setSenderType,
  } = useChef();
  const { currentUser } = useAuth();
  const { formatDateUS, formatTimeUS } = useFormattedUtils();
  const [selectedOrderType, setSelectedOrderType] = useState<number>(1);
  const [filteredOrders, setFilteredOrders] = useState<OrderProps[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<Status[]>([]);
  const chefId = currentUser?.uuid;
  const [newMessage, setNewMessage] = useState("");
  const [openChatOrderId, setOpenChatOrderId] = useState<string | null>(null);
  const [trackingInfo, setTrackingInfo] = useState<Record<string, Track>>({});
  const [countdowns, setCountdowns] = useState<Record<string, string>>({});
  const [notificationsSent, setNotificationsSent] = useState<Record<string, boolean>>({});
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<OrderProps | null>(null);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [trackingOrder, setTrackingOrder] = useState<OrderProps | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderProps | null>(null);
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [showBankAlert, setShowBankAlert] = useState(false);
  const [hasCheckedBank, setHasCheckedBank] = useState(false);

  const openTrackingModal = (order: OrderProps) => {
    setTrackingOrder(order);
    setIsTrackingModalOpen(true);
  };
  const handleRejectClick = (order: OrderProps) => {
    setSelectedOrder(order);
    setShowRejectModal(true);
  };
  const handleRejectCancel = () => {
    setShowRejectModal(false);
    setSelectedOrder(null);
    setSelectedReason("");
  };

  // const handleRejectConfirm = () => {
  //   if (selectedOrder && selectedReason.trim()) {
  //     handleOrderStatusChange(selectedOrder, "Cancel Order", selectedReason);
  //     setShowRejectModal(false);
  //     setSelectedOrder(null);
  //     setSelectedReason("");
  //   }
  // };

  const closeTrackingModal = () => {
    setTrackingOrder(null);
    setIsTrackingModalOpen(false);
  };

  // CHECK BANK DETAILS IF ORDERS EXIST
  useEffect(() => {
    if (orders.length > 0 && currentUser?.uuid && !hasCheckedBank) {
      listBankData(currentUser.uuid)
        .then((res) => {
          if (!res?.data || res.data.length === 0) {
            setShowBankAlert(true);
          }
          setHasCheckedBank(true);
        })
        .catch((err) => console.error("Error checking bank details:", err));
    }
  }, [orders, currentUser, hasCheckedBank]);
  useEffect(() => {
    if (!chefId) return;

    // Listen for order status updates from server
    socket.on("orderStatusChanged", (data) => {
      console.log(" Order status changed:", data);

      listOrdersData(chefId).then((res) => {
        setOrders(res?.data);
        setLoading(false);
      });
    });
    return () => {
      socket.off("updateOrderStatus");
    };
  }, [chefId]);

  const trackIds = [5, 10, 12];

  useEffect(() => {
    if (chefId) {
      socket.emit("register", { uuid: chefId, type: "chef" });
    }
  }, [chefId]);
  useEffect(() => {
    socket.on("newChatMessage", (msg: any) => {
      // Only update messages if the current chat is open
      if (msg.order_id === openChatOrderId) {
        setMessages((prev) => [
          ...prev,
          {
            type: msg.sender_type === "chef" ? "outgoing" : "incoming",
            message: msg.message,
            time: msg.time || new Date().toLocaleTimeString(),
            date: msg.date || new Date().toLocaleDateString(),
            order_id: msg.order_id,
          },
        ]);
      }
    });

    return () => {
      socket.off("newChatMessage");
    };
  }, [openChatOrderId]);



  const toggleChat = (orderId: string, chefId: string, deliveryDriverId: string) => {
    if (!deliveryDriverId) return;

    setOrderId(orderId);

    if (openChatOrderId === orderId) {
      setOpenChatOrderId(null);
    } else {
      setOpenChatOrderId(orderId);
      setSenderType("chef");
      setReceiverType("driver");

      ViewChatMessage(orderId, "chef", "driver")
        .then((res) => {
          if (res?.status && res?.data?.length > 0) {
            // Take the first chat record and flatten its messages
            const chat = res.data[0];
            const fetchedMessages = chat.conversation_messages.map((m: any) => {
              const createdAt = new Date(m.createdAt);
              const time = createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
              const date = createdAt.toLocaleDateString([], { month: 'short', day: '2-digit', year: 'numeric' });

              return {
                type: m.sender_type === "chef" ? "outgoing" : "incoming",
                message: m.message,
                time,
                date,
                order_id: chat.order_id,
              };
            });

            setMessages(fetchedMessages);
          } else {
            setMessages([]);
          }
        })
        .catch((err) => console.error("Error fetching chat messages:", err));
    }
  };


  const sendMessage = (orderId: string, chefId: string, deliveryDriverId: string) => {
    if (!deliveryDriverId || !newMessage.trim()) return;

    const data = {
      sender_uuid: chefId,
      sender_type: "chef",
      receiver_type: "driver",
      receiver_uuid: deliveryDriverId,
      message: newMessage,
      order_id: orderId,
    };
    socket.emit("updateDriverChatMessage", data, (ack: any) => {
      if (ack.status) {
        const { formattedDate, formattedTime } = formatDateTime(new Date());

        setMessages((prev) => [
          ...prev,
          {
            type: "outgoing",
            order_id: orderId,
            message: data.message,
            time: formattedTime,
            date: formattedDate,
          },
        ]);

        setNewMessage("");
      } else {
        toast.error(ack.message);
      }
    });
  };


  useEffect(() => {
    getStatus()
      .then((res) => {
        const statusData = res?.data?.data.map((s: any) => ({
          ...s,
          active: s.name === "preparing",
        }));
        setStatus(statusData);
      })
      .catch((error) => {
        console.error("Error fetching status:", error);
      });
  }, []);

  const handleOrderStatusChange = async (order: OrderProps, action: string, reason: string) => {
    try {
      let newOrderStatusId: number;
      let orderMessage: string;

      switch (action) {
        case "Preparing":
          newOrderStatusId = 3;
          orderMessage = "is now being prepared";
          break;
        case "Order Ready":
          newOrderStatusId = 4;
          orderMessage = "is ready for pickup";
          break;
        case "Cancel Order":
          newOrderStatusId = 7;
          orderMessage = "has been canceled";
          break;
        default:
          console.warn("Unknown action:", action);
          return;
      }

      const data = {
        status: newOrderStatusId,
        userId: order.user.uuid,
        orderId: order.uuid,
        chefId,
        reason: reason || null
      };


      socket.emit("updateOrderStatus", data, (res: any) => {
        if (res?.status === true) {
          toast.success(`Order ${order.orderId} ${orderMessage} successfully`, {
            autoClose: 5000,
          });
        } else {
          toast.error(res?.message, { autoClose: 5000 });
        }
      });

      listOrdersData(chefId).then((res) => {
        setOrders(res?.data);
        setLoading(false);
      });
      navigate("/orders/view");
    } catch (error) {
      toast.error(`Something went wrong. Please try again later`, {
        autoClose: 5000,
      });
      console.error("Error handling order status change:", error);
    }
  };

  // Function to show confirmation modal for scheduled order
  const showScheduledOrderModal = (order: OrderProps, message: string) => {
    setCurrentOrder(order);
    setNotificationMessage(message);
    setShowConfirmationModal(true);
  };

  // Function to handle confirmation
  const handleConfirmation = (confirmed: boolean) => {
    setShowConfirmationModal(false);
    if (confirmed && currentOrder) {
      // Handle the action when user confirms
      // For example, you might want to update the order status
      handleOrderStatusChange(currentOrder, "Preparing", "");

    }
  };

  // Function to calculate and update countdowns
  const updateCountdowns = () => {
    const newCountdowns: Record<string, string> = {};
    const newNotificationsSent = { ...notificationsSent };

    const now = moment(); // local time

    filteredOrders.forEach((order) => {
      if (
        order.order_type === 3 &&
        order.scheduled_date &&
        order.scheduled_time
      ) {
        // 1) Take only the DATE part of scheduled_date (localised)
        const localDate = moment(order.scheduled_date).format("YYYY-MM-DD");

        // 2) Combine date + scheduled_time into one DateTime (local)
        const scheduledDateTime = moment(
          `${localDate} ${order.scheduled_time}`,
          "YYYY-MM-DD HH:mm:ss"
        );

        const diffMs = scheduledDateTime.diff(now);

        if (diffMs > 0) {
          const duration = moment.duration(diffMs);

          const days = Math.floor(duration.asDays());
          const hours = duration.hours();
          const minutes = duration.minutes();

          newCountdowns[order.uuid] = `${days}d ${hours}h ${minutes}m`;

          const remainingHours = duration.asHours();
          const remainingMinutes = duration.asMinutes();

          // 🔔 Your existing notification logic
          if (remainingHours <= 24 && !newNotificationsSent[`24h_${order.uuid}`]) {
            showScheduledOrderModal(
              order,
              `Order ${order.orderId} is scheduled in less than 24 hours.`
            );
            newNotificationsSent[`24h_${order.uuid}`] = true;
          }
          if (remainingHours <= 6 && !newNotificationsSent[`6h_${order.uuid}`]) {
            showScheduledOrderModal(
              order,
              `Order ${order.orderId} is scheduled in less than 6 hours.`
            );
            newNotificationsSent[`6h_${order.uuid}`] = true;
          }
          if (remainingHours <= 2 && !newNotificationsSent[`2h_${order.uuid}`]) {
            showScheduledOrderModal(
              order,
              `Order ${order.orderId} is scheduled in less than 2 hours.`
            );
            newNotificationsSent[`2h_${order.uuid}`] = true;
          }
          if (remainingHours <= 1 && !newNotificationsSent[`1h_${order.uuid}`]) {
            showScheduledOrderModal(
              order,
              `Order ${order.orderId} is scheduled in less than 1 hour.`
            );
            newNotificationsSent[`1h_${order.uuid}`] = true;
          }
          if (remainingMinutes <= 30 && !newNotificationsSent[`30min_${order.uuid}`]) {
            showScheduledOrderModal(
              order,
              `Order ${order.orderId} is scheduled in less than 30 minutes.`
            );
            newNotificationsSent[`30min_${order.uuid}`] = true;
          }
        } else {
          newCountdowns[order.uuid] = "Time elapsed";
        }
      }
    });

    setCountdowns(newCountdowns);
    setNotificationsSent(newNotificationsSent);
  };


  // Run every minute in prod
  useEffect(() => {
    updateCountdowns();
    const interval = setInterval(updateCountdowns, 60000);
    return () => clearInterval(interval);
  }, [filteredOrders]);

  // Keep filtered orders synced with selection
  useEffect(() => {
    setFilteredOrders(
      orders.filter(
        (order: OrderProps) => order?.order_type == selectedOrderType
      )
    );
  }, [selectedOrderType, orders]);


  const getButtonLabel = (
    name: string,
    item: OrderProps
  ): (string | JSX.Element)[] => {
    const cancelButton = (
      <button
        key={`cancel-${item.uuid}`}
        onClick={() => handleRejectClick(item)} // 👈 show reason modal
        className="btn btn-danger rounded-5 small"
      >
        Cancel Order
      </button>
    );

    switch (name) {
      case "ordered":
        return ["Accept Order"];

      case "preparing":

        if (item.order_type === 3) {
          const scheduledDate = moment(item.scheduled_date).format("YYYY-MM-DD");
          const todayDate = moment().format("YYYY-MM-DD");

          if (scheduledDate === todayDate) {
            //  Delivery is TODAY → Only Order Ready
            return ["Order Ready"];
          } else {
            //  Delivery is FUTURE → Only Cancel
            return [];//add cancel button
          }
        }

        // Regular Order → Order Ready
        return ["Order Ready"];


      // Regular order
      // return ["Order Ready"];


      case "ready":
        return [];
      case "track":
        return [
          <a
            key="track"
            className="btn btn-primary rounded-5 small me-3"
            href={`https://tracking.frayo.net/?orderId=${item.uuid}&mode=driver_to_chef`}
            target="_blank"   //  opens in new tab
            rel="noopener noreferrer"
          >
            Track Order
          </a>
        ];

      case "track":
        return [
          <a
            key="track"
            className="btn btn-primary rounded-5 small me-3"
            href={`https://tracking.frayo.net/?orderId=${item.uuid}&mode=driver_to_chef`}
            target="_blank"   //  opens in new tab
            rel="noopener noreferrer"
          >
            Track Order
          </a>
          // cancelButton,
        ];

      case "delivered":
      case "canceled":
      default:
        return [];
    }
  };


  const formatString = (inputString: any) => {
    return inputString.toLowerCase().replace(/\s+/g, "-");
  };

  const openHistory = () => {
    navigate("/orders/history");
  };

  return (
    <>
      <section className="container-fluid">
        <div className="row">
          <section className="content-section">

            {/* Bank Details Missing Alert */}
            {showBankAlert && (
              <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 10000 }}>
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content p-4 text-center rounded-4">
                    <div className="mb-3">
                      <i className="material-symbols-outlined text-warning" style={{ fontSize: "48px" }}>
                        account_balance_wallet
                      </i>
                    </div>
                    <h4 className="fw-bold text-dark">Action Required</h4>
                    <p className="text-secondary mt-2 mb-4">
                      You have received an order, but you haven't added your bank details yet. <br />
                      Please add your bank details to process payments.
                    </p>

                    <div className="d-flex justify-content-center gap-3">
                      <button
                        className="btn btn-outline-secondary rounded-pill px-4"
                        onClick={() => setShowBankAlert(false)}
                      >
                        Close
                      </button>
                      <button
                        className="btn btn-primary rounded-pill px-4"
                        onClick={() => {
                          setShowBankAlert(false);
                          navigate("/settlements/add");
                        }}
                      >
                        Add Bank Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <section className="row main-content">
              <MenuOrders />

              {/* Confirmation Modal */}
              {/* <ConfirmationModal
                show={showConfirmationModal}
                onClose={() => setShowConfirmationModal(false)}
                onConfirm={handleConfirmation}
                title="Scheduled Order Notification"
                message={notificationMessage}
                confirmText="Yes, I'm ready"
               
              /> */}

              <div className="col-12 mt-md-2 mt-0">
                {/* Order Types Navigation */}
                <ul className="nav nav-pills mb-4">
                  {orderTypes.map((type: any) => (
                    <li className="nav-item" key={type.id}>
                      <button
                        className={`mt-3 nav-link ${selectedOrderType === type.id ? "active" : ""
                          } ${selectedOrderType === type.id
                            ? ""
                            : type.id === 1
                              ? "text-success border-success font-weight-bold"
                              : type.id === 3
                                ? "text-danger border-danger font-weight-bold"
                                : ""
                          } border`}
                        onClick={() => setSelectedOrderType(type.id)}
                      >
                        {`${type.name} (${orders.filter(
                          (item) =>
                            item.order_type === type.id &&
                            item.orderstatusId !== 12
                        ).length
                          })`}

                      </button>
                    </li>
                  ))}
                </ul>
                <ul
                  className="nav nav-pills mb-4 order-pills"
                  id="pills-tab"
                  role="tablist"
                >
                  {status.slice().map((s) => (
                    (s.id === 10 || s.id === 12) ? null : (
                      <li className="nav-item" role="presentation" key={s.id}>
                        <button
                          className={`mt-3 btn-sm nav-link ${s.active ? "active" : ""
                            }`}
                          id={`pills-${formatString(s.name)}-tab`}
                          data-bs-toggle="pill"
                          data-bs-target={`#pills-${formatString(s.name)}`}
                          type="button"
                          role="tab"
                          aria-controls={`pills-${formatString(s.name)}`}
                          aria-selected={s.active}
                        >
                          {`${s.name.charAt(0).toUpperCase() + s.name.slice(1)
                            } (${s.id === 5
                              ? filteredOrders.filter((item) => trackIds.includes(item.orderstatusId)).length
                              : filteredOrders.filter((item) => item.orderstatusId === s.id).length
                            })`}
                        </button>
                      </li>
                    )
                  ))}
                </ul>
                <div className="tab-content">
                  {status.slice().map((s) => (
                    (s.id === 10 || s.id === 12) ? null : (
                      <div
                        className={`tab-pane fade show${s.active ? " active" : ""
                          }`}
                        id={`pills-${s.name.toLowerCase()}`}
                        key={s.id}
                      >
                        {filteredOrders
                          .filter((item) => (s.id === 5 ? trackIds.includes(item.orderstatusId) : item.orderstatusId === s.id))
                          .map((item) => {
                            const addonsAmount = item?.orderdetails?.reduce(
                              (sum, orderDetail) => {
                                return (
                                  sum +
                                  (orderDetail?.orderdetailaddons?.reduce(
                                    (
                                      addonSum: number,
                                      addon: { quantity: number; amount: number }
                                    ) => {
                                      return (
                                        addonSum +
                                        (addon?.quantity * addon?.amount || 0)
                                      );
                                    },
                                    0
                                  ) || 0)
                                );
                              },
                              0
                            );

                            return (
                              <div
                                className="tab-pane fade show active"
                                id={`pills-${s.name.toLowerCase()}-${item.primaryId
                                  }`}
                                key={item.primaryId}
                              >
                                <div className="order-snippet mb-3">
                                  <div className="os-head">
                                    <div>
                                      <h5 className="mb-0 medium primary-color">
                                        Order - {item?.orderId}
                                      </h5>
                                      <p className="mb-0 mid-gray">
                                        <small>
                                          {formatDateUS(item?.orderDate)} - {formatTimeUS(item?.orderDate)}
                                        </small>
                                      </p>
                                      <p className="mb-0">
                                        {item?.order_type === 1 ? (
                                          <span
                                            className="badge bg-primary"
                                            style={{
                                              fontSize: "1rem",
                                              padding: "10px 20px",
                                              marginTop: "10px",
                                            }}
                                          >
                                            Regular
                                          </span>
                                        ) : item?.order_type === 3 ? (
                                          <span
                                            className="badge bg-success"
                                            style={{
                                              fontSize: "1rem",
                                              padding: "10px 20px",
                                              marginTop: "10px",
                                            }}
                                          >
                                            Scheduled
                                          </span>
                                        ) : (
                                          ""
                                        )}
                                        {/* READY → Searching for driver */}
                                        {item.orderstatusId === 4 && (
                                          <div className="mt-2">
                                            <span
                                              className="badge bg-warning text-dark"
                                              style={{ fontSize: "0.9rem", padding: "8px 12px" }}
                                            >
                                              👨‍✈️ Assingning Driver
                                            </span>
                                          </div>
                                        )}

                                        {[5, 10, 12].includes(item.orderstatusId) && (
                                          <div className="mt-2">
                                            <span className="badge bg-info text-dark" style={{ fontSize: "0.9rem", padding: "8px 12px" }}>
                                              {item.orderstatusId === 10 && " Driver Assigned"}
                                              {item.orderstatusId === 5 && "Track"}
                                              {item.orderstatusId === 12 && "On Way For User"}
                                            </span>
                                            {item.driver?.name && (
                                              <div className="mt-1 text-muted">
                                                Driver Name: 👨‍✈️ <strong>{item.driver.name}</strong>
                                              </div>
                                            )}
                                            {item.driver?.mobile && (
                                              <div className="mt-1 text-muted">
                                                Driver Contact: 📞 <strong>{item.driver.mobile}</strong>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </p>
                                    </div>
                                    <div>
                                      {item?.order_type === 3 && (
                                        <div
                                          className="card mb-3"
                                          style={{
                                            backgroundColor: "#f2f2f2",
                                            padding: "16px",
                                            borderRadius: "8px",
                                            border: "1px solid transparent",
                                            backgroundClip: "padding-box",
                                            borderImage:
                                              "linear-gradient(to right, #b2b2b2, #d9d9d9) 1",
                                          }}
                                        >
                                          <h5 className="mb-0 medium primary-color">
                                            Delivery Date
                                            <p className="mb-0 mid-gray">
                                              <small>
                                                {formatDateUS(item?.scheduled_date)} -{" "}
                                                {formatTimeUS(
                                                  item?.scheduled_date && item?.scheduled_time
                                                    ? `${item.scheduled_date.split("T")[0]}T${item.scheduled_time}`
                                                    : ""
                                                )}
                                              </small>

                                            </p>
                                            {countdowns[item.uuid] && (
                                              <p className="mb-0 text-danger">
                                                <small>
                                                  Time remaining: {countdowns[item.uuid]}
                                                </small>
                                              </p>
                                            )}
                                          </h5>
                                        </div>
                                      )}
                                    </div>

                                    <div className="order-by">
                                      <div className="avatar">
                                        <div className="thumbnail me-2">
                                          {item?.user?.userProfile ? (
                                            <Avatar
                                              src={item?.user?.userProfile}
                                              name={item?.user?.name}
                                              maxInitials={1}
                                              size="25"
                                              round
                                            ></Avatar>
                                          ) : (
                                            <Avatar
                                              name={item?.user?.name}
                                              maxInitials={1}
                                              size="25"
                                              round
                                              color="#607458"
                                              fgColor="#ffffff"
                                            ></Avatar>
                                          )}
                                        </div>
                                        <p className="mb-0 me-5">
                                          <span className="tiny mid-gray">
                                            Order by{" "}
                                          </span>
                                          <br />
                                          <small>{item?.user?.name}</small>
                                        </p>
                                        {item?.delivery_driver?.name && (
                                          <p className="mb-0 me-5">
                                            <span className="tiny mid-gray">
                                              Pick by{" "}
                                            </span>
                                            <br />
                                            <small>
                                              {item?.delivery_driver?.name}
                                            </small>
                                          </p>
                                        )}
                                        {item.orderstatusId === 5 && item.deliveryDriverId && item.deliveryType === 1 && (
                                          <a
                                            href="#"
                                            className="action-item me-2"
                                            onClick={() =>
                                              toggleChat(
                                                item?.uuid,
                                                item?.chefId,
                                                item?.deliveryDriverId
                                              )
                                            }
                                          >
                                            <span className="material-symbols-outlined">
                                              sms
                                            </span>
                                          </a>
                                        )}
                                      </div>
                                    </div>

                                    {/* Chat Box */}
                                    {openChatOrderId === item.uuid && (
                                      <div className="chat-modal-overlay">
                                        <div className="chat-container">
                                          {/* Header */}
                                          <div className="chat-header">
                                            <div className="chat-title">
                                              <span className="chat-order-label">Order Chat</span>
                                              <span className="chat-order-id">{item.orderId}</span>
                                            </div>
                                            <button
                                              className="chat-close-btn"
                                              onClick={() => setOpenChatOrderId(null)}
                                              aria-label="Close chat"
                                            >
                                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M18 6L6 18M6 6l12 12" />
                                              </svg>
                                            </button>
                                          </div>

                                          {/* Participants Info */}
                                          {/* <div className="chat-participants">
        <div className="participant-badge chef">
          <span className="badge-icon">👨‍🍳</span>
          <span className="badge-text">Chef</span>
        </div>
        <div className="participant-badge driver">
          <span className="badge-icon">🚚</span>
          <span className="badge-text">Driver</span>
        </div>
      </div> */}

                                          {/* Messages Container */}
                                          <div className="messages-container">
                                            {messages.length === 0 ? (
                                              <div className="empty-chat-state">
                                                <div className="empty-icon">💬</div>
                                                <p className="empty-text">No messages yet</p>
                                                <p className="empty-subtext">Start the conversation</p>
                                              </div>
                                            ) : (
                                              messages.map((msg, index) => (
                                                <div
                                                  key={index}
                                                  className={`message-wrapper ${msg.type === "outgoing" ? "outgoing" : "incoming"}`}
                                                >
                                                  <div className="message-bubble">
                                                    <div className="message-content">
                                                      {msg.message}
                                                    </div>
                                                    <div className="message-meta">
                                                      <span className="message-time">{msg.time}</span>
                                                      <span className="message-date">{msg.date}</span>
                                                    </div>
                                                  </div>
                                                </div>
                                              ))
                                            )}
                                          </div>

                                          {/* Input Area */}
                                          <div className="chat-input-area">
                                            <div className="input-wrapper">
                                              <input
                                                type="text"
                                                className="message-input"
                                                value={newMessage}
                                                onChange={(e) => setNewMessage(e.target.value)}
                                                placeholder="Type your message here..."
                                                onKeyPress={(e) => e.key === 'Enter' && sendMessage(item?.uuid, item?.chefId, item?.deliveryDriverId)}
                                              />
                                              <button
                                                className="send-button"
                                                onClick={() => sendMessage(item?.uuid, item?.chefId, item?.deliveryDriverId)}
                                                disabled={!newMessage.trim()}
                                              >
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                                </svg>
                                                <span>Send</span>
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  <div className="row os-body mt-3">
                                    <div className="col-lg-6 col-12">
                                      <h6 className="mb-2">Item Details</h6>
                                      <div className="bg-blk">
                                        {item?.orderdetails.map(
                                          (orderDetail, j) => (
                                            <div className="row mb-3" key={j}>
                                              <div className="col-8">
                                                <div className="dish">
                                                  <div className="thumbnail me-2">
                                                    <img
                                                      src={
                                                        orderDetail.dish
                                                          ?.dishImage
                                                      }
                                                      alt={orderDetail.dish?.name}
                                                    />
                                                  </div>
                                                  <p className="mb-0">
                                                    <span className="fw-500">
                                                      {orderDetail.quantity}
                                                    </span>{" "}
                                                    x {orderDetail.dish?.name}
                                                  </p>
                                                </div>
                                              </div>
                                              <div className="col-4 d-flex align-items-center justify-content-end">
                                                <h6 className="mb-0">
                                                  $
                                                  {(
                                                    orderDetail?.quantity *
                                                    orderDetail?.amount
                                                  ).toFixed(2)}
                                                </h6>
                                              </div>
                                              {orderDetail?.orderdetailaddons
                                                ?.length > 0 && (
                                                  <div className="row mb-3">
                                                    <div className="addons-container">
                                                      <p className="fw-500 pt-2">
                                                        Addons
                                                      </p>
                                                      {orderDetail?.orderdetailaddons?.map(
                                                        (addon, index) => (
                                                          <div
                                                            key={index}
                                                            className="row align-items-center mb-2"
                                                          >
                                                            <div className="col-8">
                                                              <p className="mb-0">
                                                                <span className="fw-500">
                                                                  {addon?.quantity}
                                                                </span>{" "}
                                                                x{" "}
                                                                {
                                                                  addon
                                                                    ?.addons_master
                                                                    ?.name
                                                                }
                                                              </p>
                                                            </div>
                                                            <div className="col-4 d-flex align-items-center justify-content-end">
                                                              <h6 className="mb-0 small">
                                                                $
                                                                {(
                                                                  addon?.quantity *
                                                                  addon?.amount
                                                                )?.toFixed(2)}
                                                              </h6>
                                                            </div>
                                                          </div>
                                                        )
                                                      )}
                                                    </div>
                                                  </div>
                                                )}
                                            </div>
                                          )
                                        )}
                                        <div className="row mb-3">
                                          <p className="px-0 mb-1">
                                            <small className="fw-500 px-2">
                                              Cooking Instructions
                                            </small>
                                          </p>
                                          <p className="px-0 mb-0 px-2">
                                            <span className="tiny mid-gray">
                                              {item.cookingInstruction}
                                            </span>
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    {!openChatOrderId && (
                                      <div className="col-lg-6 col-12">
                                        <h6 className="mb-md-2 mb-0">&nbsp;</h6>
                                        <div
                                          className="accordion bill-toggle px-0"
                                          id="accordionExample"
                                        >
                                          <div className="accordion-item">
                                            <h2
                                              className="accordion-header"
                                              id={`heading-collapse-${item.uuid}`}
                                            >
                                              <button
                                                className="accordion-button collapsed"
                                                type="button"
                                                data-bs-toggle="collapse"
                                                data-bs-target={`#collapse-${item.uuid}`}
                                                aria-expanded="false"
                                                aria-controls={`collapse-${item.uuid}`}
                                              >
                                                <h6 className=" mb-0">
                                                  Bill Details
                                                  <span>
                                                    $
                                                    {(
                                                      item?.amount +
                                                      (addonsAmount || 0) +
                                                      item?.tax -
                                                      (item?.couponAmount || 0) -
                                                      (item?.amount +
                                                        (addonsAmount || 0) +
                                                        item?.tax -
                                                        (item?.couponAmount ||
                                                          0)) *
                                                      0.2 +
                                                      item?.chefTips
                                                    )?.toFixed(2)}
                                                    <span className="badge bg-primary ms-1">
                                                      PAID
                                                    </span>
                                                  </span>
                                                </h6>
                                              </button>
                                            </h2>
                                            <div
                                              id={`collapse-${item.uuid}`}
                                              className="accordion-collapse collapse"
                                              aria-labelledby={`collapse-${item.uuid}`}
                                              data-bs-parent="#accordionExample"
                                            >
                                              <div className="accordion-body">
                                                <div className="row mb-2">
                                                  <p className="col-7 mid-gray mb-0">
                                                    <small>Item Total</small>
                                                  </p>
                                                  <h6 className="col-5 mid-gray medium mb-0 amount">
                                                    $ {(item?.amount ?? 0).toFixed(2)}
                                                  </h6>
                                                </div>

                                                {item?.orderdetails?.length > 0 &&
                                                  item?.orderdetails[0]
                                                    ?.orderdetailaddons?.length >
                                                  0 && (
                                                    <div className="row mb-2">
                                                      <p className="col-7 mid-gray mb-0">
                                                        <small>Addons</small>
                                                      </p>
                                                      <h6 className="col-5 mid-gray medium mb-0 text-end">
                                                        $
                                                        {addonsAmount?.toFixed(2)}
                                                      </h6>
                                                    </div>
                                                  )}

                                                <div className="row mb-2">
                                                  <p className="col-7 mid-gray mb-0">
                                                    <small>Tax</small>
                                                  </p>
                                                  <h6 className="col-5 mid-gray medium mb-0 amount">
                                                    $ {item?.tax.toFixed(2)}
                                                  </h6>
                                                </div>
                                                <div className="row mb-2">
                                                  <p className="col-7 mid-gray mb-0">
                                                    <small>
                                                      Platform Fee (-)
                                                    </small>
                                                  </p>
                                                  <h6 className="col-5 mid-gray medium mb-0 amount text-danger">
                                                    $
                                                    {(
                                                      (item?.amount +
                                                        (addonsAmount || 0) +
                                                        item?.tax -
                                                        (item?.couponAmount ||
                                                          0)) *
                                                      0.2
                                                    ).toFixed(2)}
                                                  </h6>
                                                </div>

                                                <div className="row mb-2">
                                                  <p className="col-7 mid-gray mb-0">
                                                    <small>Discount (-)</small>
                                                  </p>
                                                  <h6 className="col-5 mid-gray medium mb-0 amount text-danger">
                                                    $
                                                    {item.couponAmount?.toFixed(
                                                      2
                                                    )}
                                                  </h6>
                                                </div>
                                                <div className="row mb-2">
                                                  <p className="col-7 mid-gray mb-0">
                                                    <small>Tip</small>
                                                  </p>
                                                  <h6 className="col-5 mid-gray medium mb-0 amount">
                                                    $ {item?.chefTips.toFixed(2)}
                                                  </h6>
                                                </div>
                                                <div className="row mb-1 mt-1">
                                                  <p className="col-7 mb-0">
                                                    Gross Grand Total
                                                  </p>
                                                  <h6 className="col-5 medium mb-0 amount">
                                                    $
                                                    {(
                                                      item?.amount +
                                                      (addonsAmount || 0) +
                                                      item?.tax -
                                                      (item?.couponAmount || 0) -
                                                      (item?.amount +
                                                        (addonsAmount || 0) +
                                                        item?.tax -
                                                        (item?.couponAmount ||
                                                          0)) *
                                                      0.2 +
                                                      item?.chefTips
                                                    )?.toFixed(2)}
                                                  </h6>
                                                </div>
                                              </div>
                                            </div>
                                            {/* {(item.orderstatusId === 4 ||
                                            item.orderstatusId === 5) && (
                                              <div className="bg-blk mt-3">
                                                <div className="ride">
                                                  <img
                                                    src="/assets/images/ride-ico.svg"
                                                    alt="ico"
                                                  />
                                                  <p className="ms-3 mb-0">
                                                    <span className="mid-gray tiny">
                                                      {item.orderstatusId === 4
                                                        ? "Searching nearby riders"
                                                        : item.orderstatusId === 5
                                                          ? "Rider on the way"
                                                          : "Order delivered"}
                                                    </span>
                                                    <br />
                                                    <strong>
                                                      OTP:{" "}
                                                      {item?.deliveryOtp ||
                                                        trackingInfo[item.uuid]
                                                          ?.deliveryOtp ||
                                                        "N/A"}
                                                    </strong>
                                                  </p>
                                                </div>
                                              </div>
                                            )} */}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    <div className="col-12 text-end mt-3">
                                      {(() => {
                                        const buttonLabels = getButtonLabel(
                                          item.orderstatus?.name,
                                          item
                                        );
                                        const validLabels = Array.isArray(
                                          buttonLabels
                                        )
                                          ? buttonLabels
                                          : [];

                                        return validLabels
                                          .map((label: any, index) => {
                                            if (typeof label === "string") {
                                              return (
                                                <button
                                                  key={index}
                                                  className={`btn btn-${label === "Cancel Order"
                                                    ? "danger"
                                                    : "primary"
                                                    } rounded-5 small me-3`}
                                                  onClick={() => {
                                                    if (label === "Cancel Order") {
                                                      handleRejectClick(item);
                                                    } else {
                                                      handleOrderStatusChange(item, label, "");
                                                    }
                                                  }}

                                                >
                                                  {label}
                                                </button>
                                              );
                                            }
                                            return label;
                                          })
                                          .filter(Boolean);
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )
                  ))}

                </div>
              </div>
              {isTrackingModalOpen && trackingOrder && (
                <div className="modal-overlay">
                  <div className="modal-content">
                    <button className="modal-close" onClick={closeTrackingModal}>
                      &times;
                    </button>

                    <LiveTrackingMap orderId={trackingOrder.uuid}
                      onClose={closeTrackingModal}
                    />


                  </div>
                </div>
              )}
              {/* {showRejectModal && (
  <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999 }}>
    <div className="modal-dialog modal-dialog-centered">
      <div className="modal-content p-3">
        
        <h5>Cancel Scheduled Order</h5>

        <label className="mt-2 fw-semibold">Select Reason</label>
        <select
          className="form-select"
          value={selectedReason || "Other"}
          onChange={(e) => {
            const v = e.target.value;
            setSelectedReason(v === "Other" ? "" : v);
          }}
        >
          <option value="Other">Other</option>
          <option value="Preparation not started">Preparation not started</option>
          <option value="Ingredient not available">Ingredient not available</option>
          <option value="Too many orders">Too many orders</option>
          <option value="Kitchen closed">Kitchen closed</option>
          <option value="Technical issue">Technical issue</option>
          <option value="Dish not available">Dish not available</option>
        </select>

        {(selectedReason === "" || selectedReason === "Other") &&
          <textarea
            className="form-control mt-3"
            placeholder="Write reason..."
            rows={3}
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value)}
          />
        }

        <div className="d-flex justify-content-end gap-3 mt-3">
          <button className="btn btn-secondary" onClick={handleRejectCancel}>
            Cancel
          </button>
          <button className="btn btn-danger" disabled={!selectedReason.trim()} onClick={handleRejectConfirm}>
            Confirm
          </button>
        </div>

      </div>
    </div>
  </div>
)} */}


            </section>

          </section>
        </div>
      </section>
    </>
  );
};

export default Orders;